import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, ChevronRight, Home, Building2, MapPin, Facebook } from "lucide-react";
import { SERVICES } from "@/data/services";
import { LOCATIONS } from "@/data/locations";
import { BUSINESS } from "@/data/businessInfo";

const COUNTIES = [
  {
    name: "St. Lucie County",
    cities: LOCATIONS.filter(l => l.county === "St. Lucie County"),
  },
  {
    name: "Martin County",
    cities: LOCATIONS.filter(l => l.county === "Martin County"),
  },
  {
    name: "Indian River County",
    cities: LOCATIONS.filter(l => l.county === "Indian River County"),
  },
  {
    name: "Palm Beach County",
    cities: LOCATIONS.filter(l => l.county === "Palm Beach County"),
  },
  {
    name: "Multi-County",
    cities: LOCATIONS.filter(l => l.county && l.county.includes("&")),
  },
].filter(c => c.cities.length > 0);

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [areasOpen, setAreasOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const loc = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#6B3A1F]/10" data-testid="main-header">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-[#401303] via-[#401303] to-[#291C59] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-10 text-xs sm:text-sm font-medium tracking-wide" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span className="hidden sm:inline">Licensed & Insured | Lic# {BUSINESS.license}</span>
          <span className="sm:hidden">Lic# {BUSINESS.license}</span>
          <div className="flex items-center gap-4">
            <span className="hidden md:inline">Open 24 Hours</span>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="flex items-center gap-1.5 text-[#D97904] hover:text-[#BF8D30] transition-colors duration-200" data-testid="header-phone-link">
              <Phone className="w-3.5 h-3.5" />
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-28 lg:h-36">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="header-logo">
            <img src={BUSINESS.logo} alt={BUSINESS.name} className="h-24 lg:h-32 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {/* Home */}
            <Link to="/" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-home">Home</Link>
            
            {/* About */}
            <Link to="/about" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/about' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-about">About</Link>

            {/* Services Dropdown */}
            <div className="relative" onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
              <Link to="/services" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg flex items-center gap-1 transition-colors duration-200 ${loc.pathname.startsWith('/services') ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-services">
                Services <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[640px]" data-testid="services-mega-menu">
                  <div className="bg-white rounded-2xl shadow-2xl border border-[#6B3A1F]/10 p-6 grid grid-cols-2 gap-6">
                    <div>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#BF8D30]">
                        <Home className="w-4 h-4 text-[#BF8D30]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#BF8D30]" style={{ fontFamily: 'Outfit, sans-serif' }}>Residential</span>
                      </div>
                      <div className="space-y-0.5">
                        {SERVICES.map(s => (
                          <Link key={s.residential.slug} to={`/services/${s.residential.slug}`} className="block px-3 py-2 text-sm text-[#401303] hover:bg-[#BF8D30]/10 hover:text-[#BF8D30] rounded-lg transition-colors duration-200" onClick={() => setServicesOpen(false)}>{s.title}</Link>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-[#317E8C]">
                        <Building2 className="w-4 h-4 text-[#317E8C]" />
                        <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#317E8C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Commercial</span>
                      </div>
                      <div className="space-y-0.5">
                        {SERVICES.map(s => (
                          <Link key={s.commercial.slug} to={`/services/${s.commercial.slug}`} className="block px-3 py-2 text-sm text-[#401303] hover:bg-[#317E8C]/10 hover:text-[#317E8C] rounded-lg transition-colors duration-200" onClick={() => setServicesOpen(false)}>{s.title}</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Service Areas Dropdown by County */}
            <div className="relative" onMouseEnter={() => setAreasOpen(true)} onMouseLeave={() => setAreasOpen(false)}>
              <Link to="/service-areas" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg flex items-center gap-1 transition-colors duration-200 ${loc.pathname.startsWith('/service-areas') ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-service-areas">
                Service Areas <ChevronDown className="w-3.5 h-3.5" />
              </Link>
              {areasOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-[700px]" data-testid="areas-mega-menu">
                  <div className="bg-white rounded-2xl shadow-2xl border border-[#6B3A1F]/10 p-6">
                    <div className="grid grid-cols-4 gap-5">
                      {COUNTIES.map(county => (
                        <div key={county.name}>
                          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#317E8C] mb-2 pb-1.5 border-b border-[#317E8C]/20" style={{ fontFamily: 'Outfit, sans-serif' }}>{county.name}</p>
                          <div className="space-y-0.5">
                            {county.cities.map(city => (
                              <Link key={city.id} to={`/service-areas/${city.id}`} className="block px-2 py-1.5 text-sm text-[#401303] hover:bg-[#317E8C]/10 hover:text-[#317E8C] rounded-md transition-colors duration-200" onClick={() => setAreasOpen(false)} data-testid={`area-menu-${city.id}`}>
                                {city.name}
                              </Link>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-3 border-t border-[#6B3A1F]/10">
                      <Link to="/service-areas" className="text-sm font-semibold text-[#BF8D30] hover:text-[#D97904] flex items-center gap-1 transition-colors duration-200" onClick={() => setAreasOpen(false)}>
                        View All Service Areas <ChevronRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Reviews */}
            <Link to="/reviews" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/reviews' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-reviews">Reviews</Link>
            
            {/* FAQ */}
            <Link to="/faq" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/faq' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-faq">FAQ</Link>
            
            {/* Blog */}
            <Link to="/blog" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/blog' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-blog">Blog</Link>
            
            {/* Contact */}
            <Link to="/contact" className={`px-3 xl:px-4 py-2 text-sm font-semibold tracking-tight rounded-lg transition-colors duration-200 ${loc.pathname === '/contact' ? 'text-[#BF8D30] bg-[#BF8D30]/10' : 'text-[#401303] hover:text-[#BF8D30] hover:bg-[#BF8D30]/5'}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="nav-contact">Contact</Link>
          </nav>

          {/* CTA buttons */}
          <div className="hidden lg:flex items-center gap-3">
            <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-full btn-gradient transition-colors duration-200 fb-glow" data-testid="header-facebook" aria-label="Follow us on Facebook">
              <Facebook className="w-5 h-5" />
            </a>
            <Link to="/book" className="px-5 py-2.5 btn-gradient text-sm rounded-full" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="header-book-btn">
              Book Service
            </Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="px-5 py-2.5 border-2 border-[#401303] text-[#401303] text-sm font-bold rounded-full hover:bg-[#401303] hover:text-white transition-colors duration-200 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="header-call-btn">
              <Phone className="w-4 h-4" /> Call Now
            </a>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-[#401303]" data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-[#6B3A1F]/10 shadow-lg max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
            <Link to="/" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>Home</Link>
            <Link to="/about" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>About</Link>

            {/* Mobile Services */}
            <div>
              <button onClick={() => setMobileServicesOpen(!mobileServicesOpen)} className="w-full flex items-center justify-between px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Services <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileServicesOpen && (
                <div className="pl-4 space-y-0.5 pb-2">
                  <p className="px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#BF8D30]">Residential</p>
                  {SERVICES.map(s => (
                    <Link key={s.residential.slug} to={`/services/${s.residential.slug}`} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-[#6B3A1F] hover:text-[#BF8D30]">{s.title}</Link>
                  ))}
                  <p className="px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#317E8C] mt-2">Commercial</p>
                  {SERVICES.map(s => (
                    <Link key={s.commercial.slug} to={`/services/${s.commercial.slug}`} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-[#6B3A1F] hover:text-[#317E8C]">{s.title}</Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Service Areas by County */}
            <div>
              <button onClick={() => setMobileAreasOpen(!mobileAreasOpen)} className="w-full flex items-center justify-between px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Service Areas <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileAreasOpen ? 'rotate-180' : ''}`} />
              </button>
              {mobileAreasOpen && (
                <div className="pl-4 space-y-0.5 pb-2">
                  {COUNTIES.map(county => (
                    <div key={county.name}>
                      <p className="px-4 py-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#317E8C]">{county.name}</p>
                      {county.cities.map(city => (
                        <Link key={city.id} to={`/service-areas/${city.id}`} onClick={() => setMobileOpen(false)} className="block px-4 py-2 text-sm text-[#6B3A1F] hover:text-[#317E8C]">{city.name}</Link>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Link to="/reviews" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>Reviews</Link>
            <Link to="/faq" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>FAQ</Link>
            <Link to="/blog" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>Blog</Link>
            <Link to="/contact" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-[#401303] font-semibold rounded-lg hover:bg-[#BF8D30]/10" style={{ fontFamily: 'Outfit, sans-serif' }}>Contact</Link>
            
            <div className="pt-4 space-y-3 border-t border-[#6B3A1F]/10">
              <Link to="/book" onClick={() => setMobileOpen(false)} className="block w-full text-center px-5 py-3 btn-gradient rounded-full" data-testid="mobile-book-btn">Book Service</Link>
              <a href={`tel:${BUSINESS.phoneRaw}`} className="block w-full text-center px-5 py-3 border-2 border-[#401303] text-[#401303] font-bold rounded-full" data-testid="mobile-call-btn">Call {BUSINESS.phone}</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
